import React from 'react';
import { formatShabbatTimeRule } from '../../shabbatZmanUtils';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const Parchment = ({ shabbat, processedCustomTimes, drashaText, loading, error, title = 'זמני שבת', showTitle = true, dayEntryTime, dayExitTime, bgColor, showTimeRule = false, showEntryExit = true, shabbatLeyning, showLeyning, showHaftarah, leyningDisplayMode }) => {
  const bg = bgColor || '#FDF6E3';

  const timeToMinutes = (t) => {
    if (!t || t === '--:--') return -1;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  if (loading) {
    return (
      <div style={{ ...S.wrap, backgroundColor: bg }}>
        <div style={S.stateText}>טוען זמני שבת...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...S.wrap, backgroundColor: bg }}>
        <div style={{ ...S.stateText, color: '#c0392b' }}>{error}</div>
      </div>
    );
  }

  if (!shabbat) {
    return (
      <div style={{ ...S.wrap, backgroundColor: bg }}>
        <div style={S.stateText}>אין נתוני שבת</div>
      </div>
    );
  }

  const parasha = shabbat.meta?.parsha || shabbat.parsha || 'פרשת השבוע';
  const entry = dayEntryTime || shabbat.meta?.candleLighting || shabbat.friday?.candleLighting || '--:--';
  const exit  = dayExitTime  || shabbat.meta?.havdalah      || shabbat.shabbat?.havdalah      || '--:--';

  const fridayTimes = (processedCustomTimes || [])
    .filter(ct => (ct.day || 'friday') === 'friday')
    .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

  const shabbatTimes = (processedCustomTimes || [])
    .filter(ct => ct.day === 'shabbat')
    .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

  return (
    <div style={{ ...S.wrap, backgroundColor: bg }} dir="rtl">
      {/* מסגרת כפולה */}
      <div style={S.outerBorder}>
        <div style={S.innerBorder}>

          {/* כותרת */}
          {showTitle && (
            <div style={S.header}>
              <span style={S.ornament}>✦</span>
              <span style={S.headerTitle}>{title}</span>
              <span style={S.ornament}>✦</span>
            </div>
          )}

          {/* פרשה */}
          <div style={S.parshaBlock}>
            {shabbat.meta?.parshaShowPrefix !== false && <div style={S.parshaLabel}>פרשת</div>}
            <div style={S.parshaName}>{parasha}</div>
            {shabbat.meta?.parshaSubtitle && <div style={S.parshaSubtitle}>{shabbat.meta.parshaSubtitle}</div>}
          </div>

          {/* קריאה */}
          {(showLeyning || showHaftarah) && shabbatLeyning && (
            <>
              <div style={S.divider}>
                <span style={S.dividerLine} />
                <span style={S.dividerOrn}>✦</span>
                <span style={S.dividerLine} />
              </div>
              <div style={{ padding: '4px 8px' }}>
                <LeyningDisplay
                  leyning={shabbatLeyning}
                  mode={leyningDisplayMode}
                  showHaftarah={showHaftarah}
                  showMaftir={leyningDisplayMode === 'full'}
                  compact={false}
                />
              </div>
            </>
          )}

          {/* מפריד */}
          {showEntryExit && (
            <div style={S.divider}>
              <span style={S.dividerLine} />
              <span style={S.dividerOrn}>✦</span>
              <span style={S.dividerLine} />
            </div>
          )}

          {/* כניסת שבת */}
          {showEntryExit && (
            <div style={S.timeRow}>
              <div style={S.timeLabel}>כניסת שבת</div>
              <div style={S.timeValue}>{entry}</div>
            </div>
          )}

          {/* מפריד קטן */}
          {showEntryExit && <div style={S.thinDivider} />}

          {/* צאת שבת */}
          {showEntryExit && (
            <div style={S.timeRow}>
              <div style={S.timeLabel}>צאת שבת</div>
              <div style={S.timeValue}>{exit}</div>
            </div>
          )}

          {/* דרשה */}
          {drashaText && (
            <>
              <div style={S.divider}>
                <span style={S.dividerLine} />
                <span style={S.dividerOrn}>✦</span>
                <span style={S.dividerLine} />
              </div>
              <div style={S.drashaText}>{drashaText}</div>
            </>
          )}

          {/* זמנים מותאמים */}
          {(fridayTimes.length > 0 || shabbatTimes.length > 0) && (
            <>
              <div style={S.divider}>
                <span style={S.dividerLine} />
                <span style={S.dividerOrn}>✦</span>
                <span style={S.dividerLine} />
              </div>
              <div style={S.customList}>
                {[...fridayTimes, ...shabbatTimes].map((ct, i) => (
                  <div key={i} style={S.customRow}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      <span style={S.customLabel}>{ct.label}</span>
                      {showTimeRule && formatShabbatTimeRule(ct) && (
                        <span style={{ fontSize: '10px', color: S.customLabel.color, opacity: 0.55, lineHeight: 1 }}>{formatShabbatTimeRule(ct)}</span>
                      )}
                    </div>
                    <span style={S.customDots} />
                    <span style={S.customTime}>{ct.time}</span>
                  </div>
                ))}
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

const BROWN   = '#3E2723';
const BROWN_L = '#6D4C41';
const GOLD    = '#B8860B';
const CREAM   = '#FDF6E3';

const S = {
  wrap: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'stretch',
    padding: '14px',
    boxSizing: 'border-box',
    fontFamily: 'serif',
  },
  outerBorder: {
    flex: 1,
    border: `3px double ${GOLD}`,
    borderRadius: '6px',
    padding: '6px',
    display: 'flex',
  },
  innerBorder: {
    flex: 1,
    border: `1px solid ${GOLD}`,
    borderRadius: '4px',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '16px',
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: BROWN,
    letterSpacing: '0.08em',
  },
  ornament: {
    color: GOLD,
    fontSize: '14px',
  },
  parshaBlock: {
    textAlign: 'center',
    marginBottom: '16px',
  },
  parshaLabel: {
    fontSize: '13px',
    color: BROWN_L,
    letterSpacing: '0.12em',
    marginBottom: '4px',
  },
  parshaName: {
    fontSize: '34px',
    fontWeight: 'bold',
    color: BROWN,
    lineHeight: 1.2,
  },
  parshaSubtitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: BROWN,
    marginTop: '4px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '14px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: GOLD,
    opacity: 0.6,
    display: 'block',
  },
  dividerOrn: {
    color: GOLD,
    fontSize: '12px',
    flexShrink: 0,
  },
  thinDivider: {
    height: '1px',
    backgroundColor: GOLD,
    opacity: 0.25,
    margin: '10px 20px',
  },
  timeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '6px 8px',
  },
  timeLabel: {
    fontSize: '15px',
    color: BROWN_L,
    fontWeight: 'bold',
    letterSpacing: '0.05em',
  },
  timeValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: BROWN,
    fontVariantNumeric: 'tabular-nums',
    direction: 'ltr',
  },
  drashaText: {
    textAlign: 'center',
    fontSize: '15px',
    color: BROWN,
    fontStyle: 'italic',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  customList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  customRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    padding: '2px 4px',
  },
  customLabel: {
    fontSize: '14px',
    color: BROWN_L,
    flexShrink: 0,
  },
  customDots: {
    flex: 1,
    borderBottom: `1px dotted ${GOLD}`,
    opacity: 0.5,
    marginBottom: '4px',
  },
  customTime: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: BROWN,
    direction: 'ltr',
    flexShrink: 0,
  },
  stateText: {
    fontSize: '16px',
    color: BROWN,
    textAlign: 'center',
    margin: 'auto',
  },
};

export default Parchment;
